/**
 * 017: structure copied from
 * vendor/isomorphic/apps/isomorphic/src/app/shared/account-settings/personal-info.tsx (the `Form`
 * + `FormFooter` shell every FORM screen in this migration uses) and
 * vendor/isomorphic/apps/isomorphic/src/app/shared/ecommerce/category/create-category.tsx (the
 * page-with-back-button + FormGroup field layout). `ecommerce/product/create-edit/product-pricing.tsx`
 * is a bare fragment of rizzui `Input`s meant to be embedded inside that flow's own parent — it
 * supplies none of this screen's structure (PageHeader, FormGroup, footer), so it is not the
 * structural source, only the origin of the bare pricing-field idiom.
 *
 * Only the data, columns and labels are ours. The screen-to-template mapping is
 * specs/017-isomorphic-theme-only/contracts/screen-map.md.
 */
import { useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { z } from "zod";
import toast from "react-hot-toast";
import { PiIdentificationBadgeDuotone as IdentityIcon, PiInfoDuotone as InfoIcon, PiPercentDuotone as PricingIcon } from "react-icons/pi";
import FormFooter from "@core/components/form-footer";
import { createTier } from "../../lib/services/catalog";
import { can } from "../../lib/permissions";
import { formCard, formCardFooter, formSections } from "../../lib/form-layout";
import { SectionTitle } from "../../components/section-title";
import type { AccountSummary, PermissionState } from "../../types";
import { PageHeader, ErrorSurface, problemTitle, Field, FormGroup, Form } from "@steelyard/theme";
import { SelectField } from "../../components/select-field";
import { DateField } from "../../components/date-field";
import { Alert } from "rizzui";
import { CheckboxCard } from "../../components/checkbox-card";
import { parseIsoDate, toIsoDate } from "../../lib/format";
import { useGoBack } from "../../lib/navigation";
import { tierPath } from "../../lib/paths";
import { PIECE_PURCHASE, ROUNDING_HINT, ROUNDING_LABEL, ROUNDING_MODES, UNIT_PURCHASE } from "../../lib/tier-text";
import { PricingFlow } from "../../components/pricing-flow";


/**
 * Ported from apps/admin/app/(app)/tiers/new/page.tsx. Cancel lands on the tier list (T059).
 *
 * A successful create goes straight to the new tier's discount rules (2026-09-29): a tier with no
 * rules prices nothing below base, so the rules are the obvious next step. The seed discount is
 * only recorded on the tier (`seedDiscountPct` "is not what any row is priced at", pricing-admin.md),
 * so it is offered there as an unsaved "All products" starter rule the admin can keep or change.
 */

const tierSchema = z.object({
  name: z.string().trim().min(1, "Required"),
  effectiveFrom: z.string().trim().min(1, "Required"),
  seedDiscountPct: z
    .string()
    .trim()
    .refine((v) => /^\d+(\.\d+)?$/.test(v), "Enter a number")
    .refine((v) => Number(v) <= 100, "Cannot exceed 100%"),
  priceRoundingMode: z.enum(["none", "rupee", "nearest_5"]),
  allowPiecePurchase: z.boolean(),
  allowUnitPurchase: z.boolean(),
});
type TierFormValues = z.infer<typeof tierSchema>;

const defaultValues: TierFormValues = {
  name: "",
  effectiveFrom: "",
  seedDiscountPct: "0",
  priceRoundingMode: "none",
  allowPiecePurchase: false,
  allowUnitPurchase: true,
};

export default function NewTier() {
  const navigate = useNavigate();
  const goBack = useGoBack("/tiers");
  const { permissionState } = useOutletContext<{ account: AccountSummary; permissionState: PermissionState }>();
  const [error, setError] = useState<unknown>(null);

  if (!can(permissionState, "pricing.administer")) {
    return <ErrorSurface error={{ title: "Not permitted", status: 403 }} action="create a tier" />;
  }

  return (
    <div className="flex flex-col gap-6 @container">
      <PageHeader title="New Tier" breadcrumb={[{ name: "Home", href: "/" }, { name: "Pricing" }, { name: "Tiers", href: "/tiers" }, { name: "New Tier" }]} />
      <PricingFlow />
      <Alert color="info" variant="flat" icon={<InfoIcon aria-hidden className="size-4" />}>
        A tier is a price list for a group of customers — a trade or fabricator rate, say. Its prices come from its
        discount rules, which you set up next.
      </Alert>

      {error ? <ErrorSurface error={error} action="create this tier" /> : null}

      <Form<TierFormValues>
        validationSchema={tierSchema}
        useFormProps={{ defaultValues }}
        className={formCard}
        onSubmit={async (values) => {
          setError(null);
          try {
            const created = (await createTier({
              name: values.name,
              seedDiscountPct: Number(values.seedDiscountPct || 0),
              allowPiecePurchase: values.allowPiecePurchase,
              allowUnitPurchase: values.allowUnitPurchase,
              priceRoundingMode: values.priceRoundingMode,
              effectiveFrom: values.effectiveFrom,
            })) as { id?: string } | undefined;
            toast.success("Tier created — now set its discount rules");
            const seed = Number(values.seedDiscountPct || 0);
            if (created?.id) navigate(`${tierPath(created.id)}/rules${seed > 0 ? `?starter=${seed}` : ""}`, { replace: true });
            else goBack();
          } catch (err) {
            setError(err);
            toast.error(problemTitle(err, "Could not create the tier"));
          }
        }}
      >
        {({ register, watch, setValue, formState: { errors, isSubmitting } }) => (
          <>
            <div className={formSections}>
              <FormGroup
                title={<SectionTitle icon={IdentityIcon}>Tier details</SectionTitle>}
                description="What it's called, when it starts applying, and how its prices round."
              >
                  <Field label="Name" required placeholder="Fabricator" error={errors.name?.message} {...register("name")} />
                  <DateField
                    required
                    dateFormat="dd-MMM-yyyy"
                    placeholderText="Choose a date…"
                    selected={parseIsoDate(watch("effectiveFrom") || null)}
                    onChange={(date) => setValue("effectiveFrom", date ? toIsoDate(date as Date) : "", { shouldValidate: true })}
                    inputProps={{
                      label: "Effective from",
                      helperText: "Until this date the tier is administrable but nothing it holds is offered for sale.",
                      error: errors.effectiveFrom?.message,
                      clearable: Boolean(watch("effectiveFrom")),
                      onClear: () => setValue("effectiveFrom", "", { shouldValidate: true }),
                    }}
                  />
                  <Field
                    label="Starting discount (%)"
                    required
                    inputMode="decimal"
                    hint="Recorded on the tier, and offered as a first “All products” rule on the next screen. Only rules set prices."
                    error={errors.seedDiscountPct?.message}
                    {...register("seedDiscountPct")}
                  />
                  <SelectField label="Rounding mode" required hint={ROUNDING_HINT} error={errors.priceRoundingMode?.message} {...register("priceRoundingMode")}>
                    {ROUNDING_MODES.map((mode) => (
                      <option key={mode} value={mode}>
                        {ROUNDING_LABEL[mode]}
                      </option>
                    ))}
                  </SelectField>
              </FormGroup>

              <FormGroup title={<SectionTitle icon={PricingIcon}>Purchase types</SectionTitle>} description="Which units customers on this tier may buy in. Prices are still generated for every unit.">
                <fieldset className="col-span-full grid grid-cols-1 gap-4 @xl:grid-cols-2 @5xl:grid-cols-3">
                  <legend className="sr-only">Purchase types</legend>
                  <CheckboxCard {...PIECE_PURCHASE} {...register("allowPiecePurchase")} />
                  <CheckboxCard {...UNIT_PURCHASE} {...register("allowUnitPurchase")} />
                </fieldset>
              </FormGroup>
            </div>

            <FormFooter
              className={formCardFooter}
              submitBtnText={isSubmitting ? "Creating…" : "Create tier"}
              isLoading={isSubmitting}
              altBtnText="Cancel"
              handleAltBtn={goBack}
            />
          </>
        )}
      </Form>
    </div>
  );
}
